import styled from 'styled-components';
import * as api from '../../../../../../../../api';
import CheckBox from './booleanFilter';
import { camelCaseToWords } from '../../../../../../../../utils/message';
import { ProviderFilters } from './common';
import MultiOptionFilter from './multiOptionFilter';
import { isEnumMember } from 'common/dist/utils/types';
import { INVERTED_LANGUAGE_MAP } from '../../../../../../../../utils/provider';

interface Props {
  activeFilters: ProviderFilters;
  dynamicActiveFilters: string[];
  onChange: (filters: ProviderFilters) => void;

  availablePractices: Pick<api.Practice.Practice, 'id' | 'name'>[];
  availableLanguages: api.Language.LanguageWithOther[];
}

const Filters: React.FC<Props> = ({
  activeFilters,
  onChange,
  dynamicActiveFilters,
  availablePractices,
  availableLanguages,
}) => {
  return (
    <FiltersContainer>
      <CheckBox
        label="Accepts new patients"
        isActive={!!activeFilters.acceptsNewPatients}
        onChange={(acceptsNewPatients) =>
          onChange({ ...activeFilters, acceptsNewPatients })
        }
      />
      <CheckBox
        label="Offers online scheduling"
        isActive={!!activeFilters.offersOnlineScheduling}
        onChange={(offersOnlineScheduling) =>
          onChange({ ...activeFilters, offersOnlineScheduling })
        }
      />
      <CheckBox
        label="Offers virtual visits"
        isActive={!!activeFilters.offersVirtualVisits}
        onChange={(offersVirtualVisits) =>
          onChange({ ...activeFilters, offersVirtualVisits })
        }
      />
      <CheckBox
        label="Able to schedule"
        isActive={!!activeFilters.ableToSchedule}
        onChange={(offersVirtualVisits) =>
          onChange({ ...activeFilters, offersVirtualVisits })
        }
      />
      <MultiOptionFilter
        label="Practices"
        activeOptions={activeFilters.practiceNames ?? []}
        options={availablePractices.map(({ name }) => name)}
        onChange={(practiceNames) =>
          onChange({ ...activeFilters, practiceNames })
        }
      />
      <MultiOptionFilter
        label="All languages"
        activeOptions={
          activeFilters.spokenLanguages?.map(
            (language) => api.Language.languageMap[language],
          ) ?? []
        }
        options={availableLanguages.map(
          (language) => api.Language.languageMap[language],
        )}
        onChange={(spokenLanguages) => {
          onChange({
            ...activeFilters,
            spokenLanguages: spokenLanguages
              .map((lang) => INVERTED_LANGUAGE_MAP[lang])
              .filter(
                (
                  language: string,
                ): language is api.Language.LanguageWithOther =>
                  isEnumMember(api.Language.LanguageWithOther, language),
              ),
          });
        }}
      />
      <MultiOptionFilter
        label="Any gender"
        activeOptions={activeFilters.sexes ?? []}
        options={Object.values(api.Patient.Sex)}
        onChange={(sexes) =>
          onChange({
            ...activeFilters,
            sexes: sexes.filter((sex: string): sex is api.Patient.Sex =>
              isEnumMember(api.Patient.Sex, sex),
            ),
          })
        }
      />
      <MultiOptionFilter
        label="All specialties"
        activeOptions={
          activeFilters.specialty
            ? [
                api.Common.SpecialtyMap[
                  activeFilters.specialty as api.Encounter.Specialty
                ],
              ]
            : []
        }
        options={Object.values(api.Encounter.Specialty).map(
          (specialty) => api.Common.SpecialtyMap[specialty],
        )}
        onChange={(specialties) =>
          onChange({
            ...activeFilters,
            specialty: specialties.length
              ? api.Common.InvertedSpecialtyMap[
                  specialties[specialties.length - 1]
                ]
              : undefined,
          })
        }
      />
      {dynamicActiveFilters.map((dynamicFilter) => (
        <CheckBox
          key={dynamicFilter}
          label={camelCaseToWords(dynamicFilter)}
          isActive={true}
          onChange={() =>
            onChange({
              ...activeFilters,
              dynamicActiveFilters: dynamicActiveFilters.filter(
                (value) => value !== dynamicFilter,
              ),
            })
          }
        />
      ))}
    </FiltersContainer>
  );
};

const FiltersContainer = styled.div`
  display: flex;
  margin-bottom: 15px;
  flex-wrap: wrap;
  gap: 5px;
`;

export default Filters;
